import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { createPortal } from "react-dom";
import { AlertTriangle, Clock, Package, Calendar, Zap, RefreshCw, Search, ChevronRight, X } from "lucide-react";
import "./production_monitoring.css";
import { ShopeeCancelPanel } from "./shopee_cancel_monitor";
import { ShopeeShippingReport, ShippingReportTile } from "./shopee_shipping_report";
import { JenisPengerjaanBadge, resolveJenisPengerjaan } from "./jenis_pengerjaan_badge";
import { MockupPreviewModal } from "./mockup_preview";
import { ShopeeMockupModal } from "./shopee_mockup_modal";
import { useAutoRefresh } from "./use_auto_refresh";
import { ShipTodayPdfButton } from "./ship_today_pdf";
import { OfflineDetailModal } from "./offline_monitor_detail";
import { OfflineHistoryPanel, ShellaStatusLine, NOTA_LABEL } from "./offline_history_panel";
import { PicPicker, PicSummary, PicRolesLine, ShopeeHistoryPanel, BordirTeamLine } from "./shopee_pic_filter";
import { SimpleIndicators, SyncWarning, SimpleFilters, DataInfoPanel } from "./monitor_simple";
import { OrderTrackResults } from "./order_track";
import { ChannelTag, takePendingScan, getChannel } from "./channel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Iter 123 — Halaman Pemantauan Produksi untuk tim Gudang.
 *  Iter 315 — Reskin dark-mode (Rido/Gudang). CSS di-scope pada `.monitor-v2` agar
 *  halaman/mode lain tidak terdampak. Logika deadline, auto-refresh, filter, dan
 *  mockup-preview DIPERTAHANKAN.
 *  Iter 496 — Terima prop `source` ("offline" | "shopee"). Komponen identik
 *  secara UI; hanya URL query param ikut source. Cache key dipisah per source
 *  supaya pergantian tab tidak mencemari data.
 */
const DIVISIONS = ["Mockup", "Gudang", "Bordir", "Sablon/DTF", "Press", "Sublim", "Finishing", "Toko"];
const PAGE_SIZE = 30;
const RETRY_DELAYS = [800, 2000];
const FAIL_LOG_KEY = "monitor_fail_log_v1";
const sleep = (ms) => new Promise(r => setTimeout(r, ms));
const EMPTY = { orders: [], summary: { terlambat: 0, hari_ini: 0, mendekat: 0, aman: 0, tanpa_deadline: 0, semua: 0 },
  division_counts: {}, unassigned_count: 0, page: 1, pages: 1, total_rows: 0, total_nota: 0 };

// Iter 578 — Catat kegagalan di localStorage, kirim ke backend saat koneksi pulih.
function logFailure(entry) {
  try {
    const arr = JSON.parse(localStorage.getItem(FAIL_LOG_KEY) || "[]");
    arr.push(entry);
    localStorage.setItem(FAIL_LOG_KEY, JSON.stringify(arr.slice(-20)));
  } catch {}
  console.warn("[Pantau Produksi] gagal memuat", entry);
}
async function flushFailures() {
  let arr = [];
  try { arr = JSON.parse(localStorage.getItem(FAIL_LOG_KEY) || "[]"); } catch {}
  if (!arr.length) return;
  try {
    await axios.post(`${API}/production/monitoring/client-errors`, { entries: arr }, { withCredentials: true });
    localStorage.removeItem(FAIL_LOG_KEY);
  } catch {}
}

const mpName = () => (getChannel() === "tokopedia" ? "Tokopedia" : "Shopee");
const fmtTime = (ms) => {
  if (!ms) return "";
  const d = new Date(ms); const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${pad(d.getHours())}.${pad(d.getMinutes())}`;
};
const fmtDeadlineDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d)) return iso;
  const months = ["Jan","Feb","Mar","Apr","Mei","Jun","Jul","Agu","Sep","Okt","Nov","Des"];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
};
const deadlineHint = (o, isShopee) => {
  const dl = o.days_left;
  const h = o.hours_left;
  if (isShopee && h !== null && h !== undefined) {
    if (h <= 0) return h > -24 ? `Terlambat ${Math.max(1, Math.round(-h))} jam` : `Terlambat ${Math.floor(-h / 24)} hari`;
    if (h <= 24) return `Sisa ${Math.max(1, Math.floor(h))} jam`;
    return `Sisa ${Math.floor(h / 24)} hari`;
  }
  if (dl === null || dl === undefined) return isShopee ? "Batas kirim belum tersedia" : "Deadline tidak diketahui";
  if (o.deadline_status === "Terlambat") return dl === 0 ? "Lewat batas kirim hari ini" : `Terlambat ${Math.abs(dl)} hari`;
  if (o.deadline_status === "Hari Ini") return "Hari ini";
  if (o.deadline_status === "Hampir Deadline") return `${dl} hari lagi`;
  return dl < 0 ? "Lewat (produksi tuntas)" : `${dl} hari lagi`;
};
const STAGE_TEXT = {
  "Mockup": ["Menunggu pengerjaan mockup", "Mockup sedang dikerjakan"],
  "Gudang": ["Menunggu barang disiapkan", "Barang sedang disiapkan"],
  "Bordir": ["Menunggu antrean bordir", "Sedang dibordir"],
  "Sablon/DTF": ["Menunggu antrean sablon", "Sedang disablon"],
  "Sablon Plastisol": ["Menunggu antrean sablon", "Sedang disablon"],
  "Mesin Press": ["Menunggu antrean press", "Sedang di-press"],
  "Jersey/Sublim": ["Menunggu antrean sublim", "Sedang disublim"],
  "Finishing": ["Menunggu antrean finishing", "Sedang di-finishing"],
  "Toko": ["Menunggu di toko", "Menunggu di toko"],
  "Selesai": ["Produksi selesai", "Produksi selesai"],
  "Perlu Diperiksa": ["Menunggu diperiksa Owner/Admin", "Menunggu diperiksa Owner/Admin"],
};
// Status proses dari data aktual: Shopee = stage_detail backend; Offline = tahap + ada/tidaknya PIC.
function processText(o, isShopee) {
  if (isShopee) return o.products?.[0]?.stage_detail || "—";
  if (NOTA_LABEL[String(o.order_status)]) return NOTA_LABEL[String(o.order_status)];
  const parts = [];
  for (const p of (o.products || [])) {
    if (p.divisions && p.divisions.length && (p.stage === "Persiapan Paralel" || p.stage === "Sablon Paralel")) {
      for (const d of p.divisions) parts.push(`${d.label}${d.pic ? "" : " (menunggu)"}`);
      continue;
    }
    const t = STAGE_TEXT[p.stage];
    const hasPic = p.pic && p.pic !== "—";
    parts.push(t ? t[hasPic ? 1 : 0] : (p.stage || "—"));
  }
  const uniq = [...new Set(parts)];
  return uniq.length ? uniq.join(" · ") : "—";
}

// Status singkat per divisi: label backend (Shopee) atau tahap Offline + ada/tidaknya petugas.
function divLabel(d, o, isShopee) {
  const lbl = (d.labels || [])[0] || "";
  if (isShopee) return lbl || "—";
  const t = STAGE_TEXT[lbl];
  if (t) return t[d.pics.length ? 1 : 0];
  return d.pics.length ? `${lbl} (sedang dikerjakan)` : `${lbl} (menunggu)`;
}

function SummaryTiles({ summary, status, setStatus, scopeText, isShopee, cancelCount, cancelOpen, onCancel, overall, coverage }) {
  const todaySub = isShopee ? [summary.lewat_jam_hari_ini ? `${summary.lewat_jam_hari_ini} lewat jam batas` : "",
    `${summary.kurang_24j || 0} sisa < 24 jam`].filter(Boolean).join(" · ") : "";
  const tiles = [
    { k: "overdue", label: "Terlambat", sub: isShopee ? "batas kirim sebelum hari ini (WIB)" : "", v: summary.terlambat, tone: "late", Icon: AlertTriangle },
    { k: "today", label: isShopee ? "Kirim Hari Ini" : "Deadline Hari Ini", sub: todaySub, v: summary.hari_ini, tone: "today", Icon: Zap },
    ...(isShopee ? [{ k: "near", label: "Kirim Besok", sub: "batas kirim tanggal besok (WIB)", v: summary.mendekat, tone: "today", Icon: Calendar }] : []),
  ];
  return (
    <div className="pm-summary" data-testid="monitor-summary">
      {tiles.map(t => (
        <button key={t.k} type="button" className={`pm-tile pm-tile-${t.tone} ${status === t.k ? "active" : ""}`}
          onClick={() => setStatus(status === t.k ? "all" : t.k)} aria-pressed={status === t.k}
          data-testid={`monitor-count-${t.k}`}>
          <t.Icon size={18} aria-hidden="true"/>
          <span className="pm-tile-label">{t.label}{t.sub && <small>{t.sub}</small>}</span>
          <strong data-testid={`monitor-count-${t.k}-value`}>{t.v}</strong>
        </button>
      ))}
      {isShopee && <button type="button" className={`pm-tile pm-tile-cancel ${cancelOpen ? "active" : ""}`} onClick={onCancel}
        aria-pressed={cancelOpen} data-testid="monitor-count-cancelled">
        <X size={18} aria-hidden="true"/><span className="pm-tile-label">Batal Perlu Tindakan<small>semua divisi · batal di {mpName()}{getChannel() === "tokopedia" ? " (dicatat manual)" : ""}</small></span>
        <strong data-testid="monitor-count-cancelled-value">{cancelCount ?? "—"}</strong></button>}
      <button type="button" className={`pm-tile pm-tile-all ${status === "all" ? "active" : ""}`}
        onClick={() => setStatus("all")} data-testid="monitor-chip-all">
        <Package size={18} aria-hidden="true"/><span className="pm-tile-label">{!isShopee ? "Semua Pekerjaan Aktif" : getChannel() === "tokopedia" ? "Pekerjaan Dipantau" : "Pesanan di Pantauan Produksi"}
          {isShopee && <small>{getChannel() === "tokopedia" ? "dihitung per pekerjaan yang sudah masuk Gudang/Produksi" : "sudah diteruskan ke Gudang"}</small>}</span><strong data-testid="monitor-chip-all-count">{summary.semua}</strong>
      </button>
      <p className="pm-scope" data-testid="monitor-scope">{scopeText}</p>
      {isShopee && <OverallNote overall={overall} coverage={coverage}/>}
    </div>
  );
}

function OverallNote({ overall, coverage }) {
  if (!overall) return null;
  const mp = mpName();
  return <p className="pm-scope pm-overall" data-testid="monitor-overall">
        <b>Seluruh pesanan {mp} aktif: {overall.active}</b> · di Gudang/produksi {overall.at_gudang} · masih di Desainer {overall.at_designer}
        {overall.direct ? ` · Tanpa Mockup ${overall.direct}` : ""} · belum masuk produksi {overall.not_in_production}
        <br/>Dari seluruhnya: terlambat {overall.overdue} · kirim hari ini {overall.today} · kirim besok {overall.tomorrow || 0}{!overall.tomorrow && overall.next_date ? ` (batas kirim resmi berikutnya ${new Date(overall.next_date + "T00:00:00+07:00").toLocaleDateString("id-ID", { weekday: "long", day: "2-digit", month: "short", timeZone: "Asia/Jakarta" })}: ${overall.next_count} pesanan)` : ""}
        {overall.no_deadline ? ` · batas kirim belum tersedia ${overall.no_deadline}` : ""} · sinkron {mp} {overall.last_sync ? `${fmtTime(new Date(overall.last_sync))} WIB` : "belum ada"}
        {coverage?.no_shopee_match ? <><br/>{coverage.no_shopee_match} pekerjaan di pantauan ini nomornya tidak cocok dengan pesanan {mp} (tampil "Tanpa Deadline")
          {coverage.no_shopee_match_breakdown ? ` — ${coverage.no_shopee_match_breakdown.numerik_bukan_shopee} nomor numerik (bukan format Shopee, kemungkinan Tokopedia) · ${coverage.no_shopee_match_breakdown.sn_belum_tersinkron} nomor Shopee belum ada di data sinkron · ${coverage.no_shopee_match_breakdown.bukan_nomor_pesanan} bukan nomor pesanan` : " — periksa nomor pesanan."}. Rekap Kirim memakai data pesanan {mpName()}, jadi pekerjaan ini tidak menghilangkan pesanan dari rekap.</> : null}
        <br/>Angka kotak mengikuti filter yang dipilih; angka pada tombol divisi = seluruh divisi. Satu kartu hanya dihitung di satu posisi.</p>;
}

function DivisionFilter({ division, setDivision, unassigned, setUnassigned, blocked, setBlocked, counts, unassignedCount, blockedCount, isShopee }) {
  return (
    <div className="pm-divs" role="group" aria-label="Filter divisi" data-testid="monitor-division-filter">
      <button type="button" className={`pm-div ${!division && !unassigned && !blocked ? "active" : ""}`}
        onClick={() => { setDivision(""); setUnassigned(false); setBlocked(false); }} data-testid="monitor-div-all">Semua Divisi</button>
      {DIVISIONS.map(d => (
        <button key={d} type="button" className={`pm-div ${division === d ? "active" : ""}`}
          onClick={() => setDivision(division === d ? "" : d)} data-testid={`monitor-div-${d.replace(/\W+/g, "-").toLowerCase()}`}>
          {d} <span>{counts[d] || 0}</span></button>
      ))}
      <button type="button" className={`pm-div pm-div-warn ${unassigned ? "active" : ""}`}
        onClick={() => setUnassigned(!unassigned)} data-testid="monitor-div-unassigned">
        Belum Ditugaskan <span>{unassignedCount || 0}</span></button>
      {isShopee && <button type="button" className={`pm-div pm-div-blocked ${blocked ? "active" : ""}`}
        onClick={() => setBlocked(!blocked)} data-testid="monitor-div-blocked"
        title="Barang belum di-ACC Gudang — belum bisa masuk antrean operator">
        Menunggu ACC Barang Gudang <span data-testid="monitor-div-blocked-count">{blockedCount || 0}</span></button>}
    </div>
  );
}

function MonitorCard({ o, isShopee, source, onDetail, onThumb, pic, clickable }) {
  const dsClass = ({ "Terlambat": "ds-late", "Hari Ini": "ds-today", "Hampir Deadline": "ds-near", "Aman": "ds-safe" })[o.deadline_status] || "ds-muted";
  const names = (o.products || []).map(p => String(p.name || "").trim()).filter(n => n && n !== "—");
  const prodText = names.length <= 2 ? names.join(" · ") : `${names[0]} · ${names[1]} +${names.length - 2} lainnya`;
  const hasQty = (o.products || []).some(p => p.quantity != null);
  const pcs = (o.products || []).reduce((s, p) => s + (p.quantity || 0), 0);
  const divs = o.divisions || [];
  const unassigned = divs.some(d => d.unassigned);
  const blocked = divs.some(d => d.blocked);
  const shopeePic = isShopee ? (o.products?.[0]?.active_pic || "") : "";
  const many = divs.length > 1;
  const picOf = (d) => d.pics.length ? d.pics.join(", ") : d.blocked ? "Belum masuk antrean operator"
    : d.unassigned ? "Belum Ditugaskan" : "Operator belum tercatat";
  const where = divs.length ? divs.map(d => `${d.division} — ${divLabel(d, o, isShopee)}${d.details?.length ? ` (${d.details.join(", ")})` : ""}`).join(" · ")
    : processText(o, isShopee);
  const lh = o.last_handler;
  const pics = divs.length ? divs.map(d => `${many ? d.division + ": " : ""}${picOf(d)}`).join(" · ")
    : (isShopee && shopeePic && shopeePic !== "Belum ditugaskan" ? shopeePic
      : !isShopee && lh ? `Terakhir ditangani ${lh.by}${lh.stage ? ` (${lh.stage})` : ""}${o.order_status === "Menunggu Finalisasi" ? " · di Shella (menunggu diterima)" : ""}`
      : "Belum ada catatan");
  const urg = (isShopee && o.past_time_today ? "LEWAT JAM BATAS" : null)
    || ({ "Terlambat": "TERLAMBAT", "Hari Ini": isShopee ? "KIRIM HARI INI" : "HARI INI", ...(isShopee ? { "Hampir Deadline": "KIRIM BESOK" } : {}) })[o.deadline_status]
    || (o.deadline_status === "Tanpa Deadline" ? (isShopee ? "BATAS KIRIM BELUM ADA" : "DEADLINE BELUM ADA") : "NORMAL");
  const n = o.order_number;
  return (
    <article className={`pm-card ${dsClass}${clickable ? " pm-card-click" : ""}`} data-testid={`monitor-card-${o.key || n}`} data-status={o.deadline_status}
      {...(clickable ? { role: "button", tabIndex: 0, title: "Klik untuk lihat rincian & riwayat",
        onClick: (e) => { if (!e.target.closest("button, a, input")) onDetail(); },
        onKeyDown: (e) => { if (e.target === e.currentTarget && e.key === "Enter") { e.preventDefault(); onDetail(); } } } : {})}>
      <div className="pm-thumb"><MonitorThumb orderNumber={n} source={source} mockupFileIds={o.mockup_file_ids} emptyReason={o.mockup_empty_reason} onOpen={onThumb}/></div>
      <div className="pm-main">
        <div className="pm-row1">
          <ChannelTag/><span className={`pm-urg ${dsClass}`} data-testid={`monitor-urgency-${n}`}>{urg}</span>
          <strong className="pm-customer" data-testid={`monitor-customer-${n}`}>{o.customer_name}</strong>
          <span className="pm-nota" data-testid={`monitor-order-number-${n}`}>{n}</span>
          {o.shopee_cancel_requested && <span className="pm-badge-cancelreq" data-testid={`monitor-cancelreq-${n}`}>Pelanggan mengajukan pembatalan</span>}
          {blocked && <span className="pm-badge-blocked" data-testid={`monitor-blocked-${n}`}>Menunggu ACC Barang Gudang</span>}
          {unassigned && <span className="pm-badge-unassigned" data-testid={`monitor-unassigned-${n}`}>Belum ada operator</span>}
        </div>
        <p className="pm-prod" title={names.join(" · ")} data-testid={`monitor-pcs-${n}`}>
          {prodText || "—"} · {isShopee && !hasQty ? <em>Belum ada data jumlah</em> : `${pcs} pcs`}</p>
        <p className="pm-line" data-testid={`monitor-stage-${n}`}><span>Sedang di</span>
          <b className={blocked ? "pm-blocked" : ""} data-testid={`monitor-division-${n}`}>{where}</b></p>
        <p className="pm-line" data-testid={`monitor-pic-${n}`}><span>Operator</span>
          <b className={divs.some(d => d.unassigned && !d.pics.length) ? "pm-warn" : ""}>{pics}</b></p>
        <JenisRow o={o}/>
        {isShopee && <BordirTeamLine o={o}/>}
        {isShopee && <PicRolesLine o={o} pic={pic}/>}
        {o.tracking_incomplete && <span className="monitor-v3-tracking-badge" data-testid={`monitor-tracking-incomplete-${n}`}
          title={o.tracking_note}>⚠ {o.tracking_note || "Status produksi belum terlacak"}</span>}
      </div>
      <div className="pm-side">
        <span className="pm-deadline-label">{isShopee ? `${o.deadline_label || `Batas Kirim ${mpName()}`}:` : "Batas selesai:"}</span>
        <span className={`pm-deadline ${dsClass}`} data-testid={`monitor-deadline-${n}`}>
          <Calendar size={13} aria-hidden="true"/> {isShopee && !o.deadline ? "Belum tersedia" : fmtDeadlineDate(o.deadline)}
          {isShopee && o.deadline_at ? ` ${o.deadline_at.slice(11, 16)} WIB` : ""}</span>
        <span className={`pm-hint ${dsClass}`} data-testid={`monitor-late-${n}`}>{deadlineHint(o, isShopee)}</span>
        {o.deadline_by && <span className="pm-hint ds-muted" data-testid={`monitor-deadline-by-${n}`}>Diatur {o.deadline_by}{o.deadline_set_at ? ` · ${new Date(o.deadline_set_at).toLocaleString("id-ID", { timeZone: "Asia/Jakarta", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })} WIB` : ""}</span>}
        {!isShopee && <ShellaStatusLine s={o.shella} testid={`monitor-shella-${n}`}/>}
        {isShopee && (o.mockup_file_ids || []).length > 0 && <button type="button" className="pm-detail-btn" onClick={onThumb}
          data-testid={`monitor-mockup-btn-${n}`}>Tinjau / Unduh Mockup <ChevronRight size={14} aria-hidden="true"/></button>}
        <button type="button" className="pm-detail-btn" onClick={onDetail} data-testid={`monitor-detail-btn-${n}`}>
          Lihat Rincian <ChevronRight size={14} aria-hidden="true"/></button>
      </div>
    </article>
  );
}

// Iter 536 — Jenis Pengerjaan per item (resolver deterministik, tidak menebak dari nama).
function JenisRow({ o }) {
  const uniq = new Map();
  for (const p of (o.products || [])) {
    const r = resolveJenisPengerjaan(p, o);
    if (!uniq.has(r.token)) uniq.set(r.token, p);
  }
  if (!uniq.size) return null;
  const list = Array.from(uniq.entries());
  return (
    <div className="pm-jenis" data-testid={`monitor-jenis-${o.order_number}`}>
      {list.slice(0, 3).map(([token, p], i) => <JenisPengerjaanBadge key={token} product={p} order={o} size="xs"
        testIdPrefix={`monitor-jenis-${o.order_number}-${i}`}/>)}
      {list.length > 3 && <span className="muted" style={{fontSize:10}}>+{list.length - 3}</span>}
    </div>
  );
}

function Pager({ data, setPage }) {
  if (data.total_rows <= data.page_size) return null;
  const from = (data.page - 1) * data.page_size + 1, to = Math.min(data.total_rows, data.page * data.page_size);
  return (
    <div className="pm-pager" data-testid="monitor-pager">
      <button type="button" disabled={data.page <= 1} onClick={() => setPage(data.page - 1)} data-testid="monitor-page-prev">‹ Sebelumnya</button>
      <span data-testid="monitor-page-info">{from}–{to} dari {data.total_rows} · Halaman {data.page}/{data.pages}</span>
      <button type="button" disabled={data.page >= data.pages} onClick={() => setPage(data.page + 1)} data-testid="monitor-page-next">Berikutnya ›</button>
    </div>
  );
}

function StatsPanel({ data, isShopee, extraStats }) {
  const [open, setOpen] = useState(false);
  const s = data.summary;
  return (
    <section className="pm-stats" data-testid="monitor-stats-panel">
      <button type="button" className="pm-stats-head" onClick={() => setOpen(v => !v)} aria-expanded={open} data-testid="monitor-stats-toggle">
        Statistik Lengkap <span aria-hidden="true">{open ? "▲" : "▼"}</span></button>
      {open && <div className="pm-stats-body" data-testid="monitor-stats-body">
        <p className="pm-stats-note">Satuan: <b>nota unik</b> · cakupan sama dengan daftar di atas (pencarian & divisi aktif).</p>
        <ul className="pm-stats-list">
          <li>Terlambat <b>{s.terlambat}</b></li><li>Deadline hari ini <b>{s.hari_ini}</b></li>
          <li>Deadline mendekat (1–2 hari) <b>{s.mendekat}</b></li>
          <li>Aman (≥3 hari, atau produksi sudah tuntas) <b>{s.aman}</b></li>
          <li>Deadline tidak diketahui <b>{s.tanpa_deadline}</b></li>
          <li>Total nota dimuat <b>{data.all_nota}</b>{data.all_rows !== data.all_nota ? ` (${data.all_rows} pekerjaan)` : ""}</li>
        </ul>
        {isShopee && data.coverage && <p className="pm-stats-note" data-testid="monitor-coverage">
          {mpName()}: {data.coverage.total} pekerjaan sudah di Gudang/produksi · {data.coverage.untracked} status Sablon/Sublim belum terlacak ·
          tidak termasuk {data.coverage.excluded_reasons?.pre_warehouse || 0} belum dikirim ke Gudang dan {data.coverage.excluded_reasons?.cancelled || 0} batal.
          Dikecualikan dari aktif: {data.coverage.excluded_reasons?.shopee_shipped || 0} sudah dikirim, {data.coverage.excluded_reasons?.shopee_cancelled || 0} batal, {data.coverage.excluded_reasons?.shopee_return || 0} retur di {mpName()}.
          Deadline = {getChannel() === "tokopedia" ? "batas kirim yang dicatat Admin di tab Tokopedia" : "batas kirim resmi Shopee"} (tingkat pesanan, bukan per resi).</p>}
        {extraStats}
      </div>}
    </section>
  );
}

export function ProductionMonitoring({ operator, onOpen, source = "offline", extraStats = null, initial = null, simple = false }) {
  const pageTitle = `Pantau Produksi ${source === "offline" ? "Offline" : getChannel() === "tokopedia" ? "Tokopedia" : "Shopee"}`;
  const src = source === "shopee" ? "shopee" : "offline";
  const isShopee = src === "shopee";
  const CACHE_KEY = `monitor_v4_cache_${src}`;
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [stale, setStale] = useState(null); // {at} bila yang tampil adalah cache
  const [lastOk, setLastOk] = useState(null);
  // `initial` — filter awal dari Pusat Kendali Owner (status/divisi/belum PIC/tertahan).
  const [status, setStatus] = useState(initial?.status || "all");
  const [division, setDivision] = useState(initial?.division || "");
  const [unassigned, setUnassigned] = useState(!!initial?.unassigned);
  const [blocked, setBlocked] = useState(!!initial?.blocked);
  const [pic, setPic] = useState(initial?.pic || "");
  const [picMode, setPicMode] = useState("current");
  const [query, setQuery] = useState(() => initial?.q || takePendingScan("track"));
  const [q, setQ] = useState(query);
  const [page, setPage] = useState(1);
  const [mockupPreview, setMockupPreview] = useState(null);
  const [shopeeDetail, setShopeeDetail] = useState(null);
  const [offlineDetail, setOfflineDetail] = useState(null);
  const reqId = useRef(0);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [shipOpen, setShipOpen] = useState(false);  // Iter 622 — Laporan Pengiriman Shopee
  const [infoOpen, setInfoOpen] = useState(false);
  const lastOkRef = useRef(null);
  const lastOkParamsRef = useRef("");

  useEffect(() => { const t = setTimeout(() => setQ(query.trim()), 300); return () => clearTimeout(t); }, [query]);
  useEffect(() => { setPage(1); }, [status, division, unassigned, blocked, pic, picMode, q, src]);
  useEffect(() => { if (!isShopee && !initial?.blocked) setBlocked(false); }, [isShopee]); // eslint-disable-line react-hooks/exhaustive-deps
  const isDefaultView = page === 1 && status === "all" && !division && !unassigned && !blocked && !q;

  const load = useCallback(async () => {
    const id = ++reqId.current;
    const params = { source: src, page, page_size: PAGE_SIZE, status, division, unassigned, blocked, q, ...(pic ? { pic, pic_mode: picMode } : {}) };
    let lastErr = null;
    for (let attempt = 0; attempt <= RETRY_DELAYS.length; attempt++) {
      try {
        const r = await axios.get(`${API}/production/monitoring`, { withCredentials: true, params, timeout: 20000 });
        if (id !== reqId.current) return;
        const now = Date.now();
        setData(r.data); setErr(""); setStale(null); setLastOk(now); lastOkRef.current = now; lastOkParamsRef.current = JSON.stringify(params); setLoading(false);
        if (isDefaultView) { try { localStorage.setItem(CACHE_KEY, JSON.stringify({ at: now, data: r.data })); } catch {} }
        flushFailures();
        return;
      } catch (e) {
        lastErr = e;
        const st = e.response?.status;
        logFailure({ at: new Date().toISOString(), status: st || "network", message: e.message, source: src, attempt: attempt + 1 });
        if (st && st < 500 && st !== 429) break; // 401/403/404: tidak perlu retry
        if (attempt < RETRY_DELAYS.length) await sleep(RETRY_DELAYS[attempt]);
      }
    }
    if (id !== reqId.current) return;
    const st = lastErr?.response?.status;
    const detail = lastErr?.response?.data?.detail;
    setErr(typeof detail === "string" ? detail : `Gagal memuat daftar pemantauan${st ? ` (kode ${st})` : " (server tidak merespons)"}.`);
    setLoading(false);
    if (!lastOkRef.current) {
      try {
        const c = JSON.parse(localStorage.getItem(CACHE_KEY) || "null");
        if (c?.data && isDefaultView) { setData(c.data); setStale({ at: c.at }); }
      } catch {}
    } else if (lastOkParamsRef.current === JSON.stringify(params)) {
      setStale({ at: lastOkRef.current });
    } else {
      // Filter/halaman berubah tapi gagal dimuat: jangan tampilkan hasil filter sebelumnya.
      setData(EMPTY); setStale(null);
    }
    throw lastErr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, page, status, division, unassigned, blocked, pic, picMode, q]);

  useEffect(() => { setData(EMPTY); setLoading(true); setLastOk(null); lastOkRef.current = null; setStale(null); setErr(""); }, [src]);
  useEffect(() => { load().catch(() => {}); }, [load]);
  useEffect(() => { const h = () => load().catch(() => {}); window.addEventListener("tkp-deadline-changed", h); return () => window.removeEventListener("tkp-deadline-changed", h); }, [load]);
  const auto = useAutoRefresh({ enabled: true, intervalMs: 30000, onRefresh: load });

  const openNota = async (n) => {
    try {
      const r = await axios.get(`${API}/orders/${encodeURIComponent(n)}`, { withCredentials: true });
      setOfflineDetail(null); onOpen(r.data);
    } catch { /* tetap di rincian */ }
  };
  // Iter 649 — Offline: rincian riwayat pengerjaan + posisi sekarang (setara Shopee).
  const openDetail = (o) => { if (isShopee) setShopeeDetail(o); else setOfflineDetail(o.order_number); };
  const scopeText = `Satuan: nota unik · ${isShopee ? `pesanan ${mpName()} yang sudah diteruskan ke Gudang (belum mencakup yang masih di Desainer / belum masuk produksi)` : "pesanan Offline aktif (belum diambil)"}`
    + `${isShopee ? " · batas kirim resmi Shopee (WIB); sudah dikirim/batal/retur tidak dihitung aktif" : ""}`
    + `${division ? ` · divisi ${division}` : " · semua divisi"}${unassigned ? " · belum ditugaskan" : ""}${blocked ? " · menunggu ACC barang Gudang" : ""}${q ? ` · cari "${q}"` : ""}`;

  const modals = <>
    {mockupPreview && mockupPreview.source === "shopee" && mockupPreview.job_id && <ShopeeMockupModal jobId={mockupPreview.job_id}
      orderNumber={mockupPreview.order_number} customerName={mockupPreview.customer_name} onClose={() => setMockupPreview(null)}/>}
    {mockupPreview && mockupPreview.source !== "shopee" && <MockupPreviewModal orderNumber={mockupPreview.order_number}
      customerName={mockupPreview.customer_name} onClose={() => setMockupPreview(null)}/>}
    {shopeeDetail && <ShopeeDetailModal o={shopeeDetail} onClose={() => setShopeeDetail(null)}/>}
    {offlineDetail && <OfflineDetailModal orderNumber={offlineDetail} onClose={() => setOfflineDetail(null)}
      onChanged={() => load().catch(() => {})} onOpenNota={onOpen ? () => openNota(offlineDetail) : null}/>}
  </>;
  const list = loading && data.orders.length === 0 ? (
      <div className="monitor-v2-empty" data-testid="monitor-loading">Memuat data pemantauan…</div>
    ) : data.orders.length === 0 && !err ? (
      <div className="monitor-v2-empty" data-testid="monitor-empty">
        {q ? `Tidak ada pesanan yang cocok dengan "${q}"${division ? ` di divisi ${division}` : ""}.${isShopee ? ` Pantauan ini hanya memuat pekerjaan yang sudah dikirim ke Gudang dan belum dikirim/batal di ${mpName()}.` : ""}` : "Tidak ada pesanan pada filter ini."}</div>
    ) : (
      <div className="pm-list" data-testid="monitor-list">
        {data.orders.map(o => <MonitorCard key={o.key || o.order_number} o={o} isShopee={isShopee} source={src}
          onDetail={() => openDetail(o)} pic={pic} clickable={simple}
          onThumb={() => setMockupPreview({ order_number: o.order_number, customer_name: o.customer_name, source: src, mockup_file_ids: o.mockup_file_ids, job_id: o.key })}/>)}
      </div>
    );
  const errBox = err && <div className="monitor-v2-error pm-error" role="alert" data-testid="monitor-error">
      <span>{err}</span>
      {stale && <span className="pm-stale" data-testid="monitor-stale">Data belum terbaru — menampilkan data terakhir {fmtTime(stale.at)} WIB.</span>}
      <button type="button" onClick={() => { setErr(""); load().catch(() => {}); }} data-testid="monitor-retry-btn">Coba Lagi</button>
    </div>;

  if (simple) return <section className="monitor-v2 monitor-v3 pm pms" data-testid="production-monitoring">
    <div className="pms-head">
      <h3 className="pms-title" data-testid="monitor-title">{pageTitle}</h3>
      <span className="monitor-v2-refresh-time" data-testid="monitor-refresh-time">
        {lastOk ? `Diperbarui ${fmtTime(lastOk)} WIB` : (loading ? "Memuat…" : "Belum berhasil dimuat")}</span>
      <button type="button" className="monitor-v2-refresh-btn" onClick={auto.refresh} disabled={auto.isRefreshing} data-testid="monitor-refresh-btn" aria-label="Muat Ulang">
        <RefreshCw size={15} className={auto.isRefreshing ? "monitor-v2-refresh-spin" : ""} aria-hidden="true"/><span>Muat Ulang</span></button>
    </div>
    <div className="monitor-v2-search pm-search pms-search">
      <Search size={20} className="monitor-v2-search-icon" aria-hidden="true"/>
      <input type="search" className="monitor-v2-search-input" autoFocus placeholder={isShopee ? "Ketik / scan nomor pesanan, nama pembeli, atau nomor resi…" : "Ketik / scan nomor nota atau nama pembeli…"}
        value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => { if (e.key === "Escape") setQuery(""); }} data-testid="monitor-search"/>
      {query && <button type="button" className="pm-search-clear" onClick={() => setQuery("")} aria-label="Hapus pencarian" data-testid="monitor-search-clear"><X size={14}/></button>}
    </div>
    <SimpleIndicators data={data} isShopee={isShopee} status={status} setStatus={(v) => { setCancelOpen(false); setStatus(v); }}
      unassigned={unassigned} setUnassigned={setUnassigned} cancelOpen={cancelOpen} setCancelOpen={setCancelOpen}/>
    {isShopee && cancelOpen && <ShopeeCancelPanel onClose={() => setCancelOpen(false)}/>}
    <ShipTodayPdfButton compact/>
    {isShopee && (!shipOpen ? <ShippingReportTile channel={getChannel()} onOpen={() => setShipOpen(true)}/> : <ShopeeShippingReport initialChannel={getChannel()} onClose={() => setShipOpen(false)}/>)}
    <SyncWarning data={data} isShopee={isShopee} onInfo={() => setInfoOpen(true)}/>
    <SimpleFilters isShopee={isShopee} data={data} division={division} setDivision={setDivision} unassigned={unassigned} setUnassigned={setUnassigned}
      blocked={blocked} setBlocked={setBlocked} status={status} setStatus={setStatus} pic={pic} setPic={setPic}
      picPicker={isShopee ? <PicPicker options={data.pic_options} pic={pic} setPic={setPic} picMode={picMode} setPicMode={setPicMode}
        unassigned={unassigned} setUnassigned={setUnassigned}/> : null}/>
    {isShopee && pic && <PicSummary s={data.pic_summary} picMode={picMode} status={status} setStatus={setStatus}/>}
    {q.length >= 3 ? <OrderTrackResults q={q} source={src} withMockup={src === "shopee"}/> : <>{errBox}{list}<Pager data={data} setPage={setPage}/></>}
    {isShopee && <details className="pms-info pms-ship" data-testid="monitor-ship-section">
      <summary className="pms-info-head">Rekap Pengiriman <small>kirim besok</small></summary>
      <div className="pms-info-body">
        <button type="button" className={`pm-div ${status === "near" ? "active" : ""}`} onClick={() => setStatus(status === "near" ? "all" : "near")}
          data-testid="monitor-ship-tomorrow">Tampilkan Kirim Besok <span>{data.summary?.mendekat ?? 0}</span></button>
      </div>
    </details>}
    {!isShopee && <OfflineHistoryPanel onOpen={(num) => setOfflineDetail(num)}/>}
    {isShopee && <ShopeeHistoryPanel division={division}/>}
    <DataInfoPanel open={infoOpen} setOpen={setInfoOpen}>
      <p className="pm-scope" data-testid="monitor-scope">{scopeText}</p>
      {isShopee && <OverallNote overall={data.overall} coverage={data.coverage}/>}
      <p className="pm-scope">Satu kartu hanya dihitung di satu posisi. Angka indikator mengikuti filter & pencarian yang aktif.</p>
      <StatsPanel data={data} isShopee={isShopee} extraStats={extraStats}/>
    </DataInfoPanel>
    {modals}
  </section>;

  return <section className="monitor-v2 monitor-v3 pm" data-testid="production-monitoring">
    <div className="pm-toolbar">
      <h3 className="pms-title" data-testid="monitor-title">{pageTitle}</h3>
      <button type="button" className="monitor-v2-refresh-btn" onClick={auto.refresh} disabled={auto.isRefreshing}
        data-testid="monitor-refresh-btn" aria-label="Muat ulang pemantauan">
        <RefreshCw size={16} className={auto.isRefreshing ? "monitor-v2-refresh-spin" : ""} aria-hidden="true"/><span>Muat Ulang</span>
      </button>
      <span className="monitor-v2-refresh-time" data-testid="monitor-refresh-time">
        {lastOk ? `Diperbarui ${fmtTime(lastOk)} WIB` : (loading ? "Memuat…" : "Belum berhasil dimuat")}</span>
    </div>

    <SummaryTiles summary={data.summary} status={status} setStatus={(v) => { setCancelOpen(false); setStatus(v); }} scopeText={scopeText}
      isShopee={isShopee} overall={data.overall} coverage={data.coverage} cancelCount={data.cancel_summary?.needs_action} cancelOpen={cancelOpen} onCancel={() => setCancelOpen(v => !v)}/>
    {isShopee && cancelOpen && <ShopeeCancelPanel onClose={() => setCancelOpen(false)}/>}
    <ShipTodayPdfButton compact/>
    {isShopee && !shipOpen && <ShippingReportTile channel={getChannel()} onOpen={() => setShipOpen(true)}/>}
    {isShopee && shipOpen && <ShopeeShippingReport initialChannel={getChannel()} onClose={() => setShipOpen(false)}/>}
    <div className="monitor-v2-search pm-search pm-search-main">
      <Search size={16} className="monitor-v2-search-icon" aria-hidden="true"/>
      <input type="search" className="monitor-v2-search-input"
        placeholder={isShopee ? "Cari nomor pesanan, pelanggan, atau produk…" : "Cari nama pelanggan atau nomor pesanan"}
        value={query} onChange={(e) => setQuery(e.target.value)} data-testid="monitor-search"/>
      {query && <button type="button" className="pm-search-clear" onClick={() => setQuery("")}
        aria-label="Hapus pencarian" data-testid="monitor-search-clear"><X size={14}/></button>}
    </div>
    {(!isShopee && pic) || (blocked && !isShopee) ? <div className="pm-active-filters" data-testid="monitor-active-filters" style={{display:"flex",gap:6,flexWrap:"wrap",margin:"0 0 8px"}}>
      {pic && <button type="button" className="soft-btn small-btn" onClick={() => setPic("")} data-testid="monitor-pic-filter">Operator: <b>{pic}</b>{division ? ` · ${division}` : ""} <X size={12}/></button>}
      {blocked && !isShopee && <button type="button" className="soft-btn small-btn" onClick={() => setBlocked(false)} data-testid="monitor-blocked-filter">Tertahan serah-terima <X size={12}/></button>}
    </div> : null}
    <DivisionFilter division={division} setDivision={setDivision} unassigned={unassigned} setUnassigned={setUnassigned}
      blocked={blocked} setBlocked={setBlocked} isShopee={isShopee}
      counts={data.division_counts || {}} unassignedCount={data.unassigned_count} blockedCount={data.blocked_count}/>
    {isShopee && <PicPicker options={data.pic_options} pic={pic} setPic={setPic} picMode={picMode} setPicMode={setPicMode}
      unassigned={unassigned} setUnassigned={setUnassigned}/>}
    {isShopee && pic && <PicSummary s={data.pic_summary} picMode={picMode} status={status} setStatus={setStatus}/>}

    {err && <div className="monitor-v2-error pm-error" role="alert" data-testid="monitor-error">
      <span>{err}</span>
      {stale && <span className="pm-stale" data-testid="monitor-stale">Data belum terbaru — menampilkan data terakhir {fmtTime(stale.at)} WIB.</span>}
      <button type="button" onClick={() => { setErr(""); load().catch(() => {}); }} data-testid="monitor-retry-btn">Coba Lagi</button>
    </div>}

    {isShopee && q.length >= 3 ? <OrderTrackResults q={q} source={src} withMockup/> : loading && data.orders.length === 0 ? (
      <div className="monitor-v2-empty" data-testid="monitor-loading">Memuat data pemantauan…</div>
    ) : data.orders.length === 0 && !err ? (
      <div className="monitor-v2-empty" data-testid="monitor-empty">
        {q ? `Tidak ada pesanan yang cocok dengan "${q}"${division ? ` di divisi ${division}` : ""}.` : "Tidak ada pesanan pada filter ini."}</div>
    ) : (
      <div className="pm-list" data-testid="monitor-list">
        {data.orders.map(o => <MonitorCard key={o.key || o.order_number} o={o} isShopee={isShopee} source={src}
          onDetail={() => openDetail(o)} pic={pic}
          onThumb={() => setMockupPreview({ order_number: o.order_number, customer_name: o.customer_name, source: src, mockup_file_ids: o.mockup_file_ids, job_id: o.key })}/>)}
      </div>
    )}
    {!(isShopee && q.length >= 3) && <Pager data={data} setPage={setPage}/>}
    {!isShopee && <OfflineHistoryPanel onOpen={(num) => setOfflineDetail(num)}/>}
    {isShopee && <ShopeeHistoryPanel division={division}/>}
    <StatsPanel data={data} isShopee={isShopee} extraStats={extraStats}/>

    {mockupPreview && mockupPreview.source === "shopee" && mockupPreview.job_id && <ShopeeMockupModal jobId={mockupPreview.job_id}
      orderNumber={mockupPreview.order_number} customerName={mockupPreview.customer_name} onClose={() => setMockupPreview(null)}/>}
    {mockupPreview && mockupPreview.source !== "shopee" && <MockupPreviewModal orderNumber={mockupPreview.order_number}
      customerName={mockupPreview.customer_name} onClose={() => setMockupPreview(null)}/>}
    {shopeeDetail && <ShopeeDetailModal o={shopeeDetail} onClose={() => setShopeeDetail(null)}/>}
    {offlineDetail && <OfflineDetailModal orderNumber={offlineDetail} onClose={() => setOfflineDetail(null)}
      onChanged={() => load().catch(() => {})} onOpenNota={onOpen ? () => openNota(offlineDetail) : null}/>}
  </section>;
}

export function ShopeeDetailModal({ o, onClose }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);
  return createPortal(
    <div className="modal-overlay modal-top" onClick={onClose} data-testid="monitor-shopee-detail-overlay">
      <div className="modal-content" style={{maxWidth:720}} onClick={e=>e.stopPropagation()}>
        <div className="modal-header">
          <h3 data-testid="monitor-shopee-detail-title">Rincian · {o.order_number}</h3>
          <button type="button" onClick={onClose} data-testid="monitor-shopee-detail-close">×</button>
        </div>
        <div className="modal-body">
          <p className="muted small" style={{marginTop:0}}>{o.customer_name || "—"}{o.products?.[0]?.route && <> · {o.products[0].route}</>}</p>
          {o.tracking_incomplete && <div className="monitor-v3-tracking-note" data-testid="monitor-shopee-detail-tracking-note">
            ⚠ {o.tracking_note || "Status produksi belum terlacak."}</div>}
          <h4 style={{margin:"12px 0 6px", fontSize:13}}>Perjalanan Produksi</h4>
          {(o.timeline || []).length === 0 ? (
            <p className="muted small" data-testid="monitor-shopee-detail-empty-timeline">Belum ada tahapan tercatat.</p>
          ) : (
            <ol className="monitor-shopee-timeline" data-testid="monitor-shopee-detail-timeline">
              {o.timeline.map((t, i) => (
                <li key={`${t.stage}-${i}`} className={`timeline-item timeline-${t.status}`} data-testid={`monitor-shopee-timeline-item-${t.stage}`}>
                  <div className="timeline-dot" aria-hidden="true"/>
                  <div className="timeline-body">
                    <strong>{t.label}</strong>
                    <div className="muted small">
                      {t.pic ? <>Operator: <b>{t.pic}</b></> : "Operator: Belum tercatat"}
                      {t.at && <> · {fmtDeadlineDate(String(t.at).slice(0, 10))}</>}
                      {t.done_at && t.done_at !== t.at && <> · Selesai: {fmtDeadlineDate(String(t.done_at).slice(0, 10))}</>}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>, document.body
  );
}

// Iter 355 — Thumbnail dengan 3 state (loading / empty / error · Coba lagi).
//   Reuse endpoint existing /orders/{n}/mockup.preview. object-fit: contain agar
//   proporsional. Klik → preview besar (read-only, tidak mengubah status).
//   Export agar dipakai juga di card Pesanan Admin Toko (Iter 356).
//   Iter 519 — Gunakan `?thumb=256` untuk mendapatkan versi resize (JPEG q72)
//     yang di-cache 24 jam di browser + server-side LRU → fetch pertama lebih
//     cepat (barcode stamp + repeat overlay dilewati; file original tetap
//     dipakai Preview besar & Cetak). Timeout 15 detik aktif saat elemen sudah
//     berada di viewport (anti stuck "Memuat…" pada koneksi lambat).
//   Iter 522 — Dukungan multi-source: props `mockupFileIds` + `source` opsional.
//     - source="shopee" dengan mockupFileIds>0 → pakai /shopee/files/{id}/thumbnail?size=256
//       (reuse cache Iter 519, scope "monitor" inline untuk Rido).
//     - Fallback ke file berikutnya saat satu gagal (multi-mockup support).
//     - Offline tetap pakai /orders/{n}/mockup.preview?thumb=256 (zero change).
export function MonitorThumb({ orderNumber, onOpen, mockupFileIds, source, emptyReason }) {
  // Iter 598 — Shopee tanpa snapshot mockup: jangan panggil endpoint Offline; tampilkan alasan.
  const shopeeEmpty = source === "shopee" && !(mockupFileIds || []).length;
  const [state, setState] = useState({ phase: shopeeEmpty ? "empty" : "loading", version: 0, busy: false });
  const [altIdx, setAltIdx] = useState(0);
  const isShopee = source === "shopee" && Array.isArray(mockupFileIds) && mockupFileIds.length > 0;
  const activeFileId = isShopee ? mockupFileIds[altIdx] : null;
  const url = isShopee
    ? `${API}/shopee/files/${encodeURIComponent(activeFileId)}/thumbnail?size=256${state.version ? `&_r=${state.version}` : ""}`
    : `${API}/orders/${encodeURIComponent(orderNumber)}/mockup.preview?thumb=256&v=${state.version}`;
  const previewUrl = isShopee
    ? `${API}/shopee/files/${encodeURIComponent(activeFileId)}/preview`
    : `${API}/orders/${encodeURIComponent(orderNumber)}/mockup.preview?v=${state.version}`;
  const btnRef = useRef(null);
  const timerRef = useRef(null);
  const visibleRef = useRef(false);
  const autoRetried = useRef(false);
  // Reset state saat sumber mockup berubah.
  useEffect(() => {
    setState({ phase: shopeeEmpty ? "empty" : "loading", version: 0, busy: false });
    setAltIdx(0);
  }, [isShopee, shopeeEmpty, (mockupFileIds || []).join("|")]); // eslint-disable-line react-hooks/exhaustive-deps
  const onClick = (e) => {
    e.stopPropagation();
    if (state.phase === "error") {
      autoRetried.current = false;
      setAltIdx(0);
      setState({ phase: "loading", version: state.version + 1 });
      return;
    }
    if (state.phase === "ok" && onOpen) onOpen(previewUrl, mockupFileIds, source);
  };
  const clearTimer = () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
  };
  const onError = () => {
    clearTimer();
    // Iter 522 — Shopee multi-mockup: coba file berikutnya sebelum mark error.
    if (isShopee && altIdx < mockupFileIds.length - 1) {
      setAltIdx(altIdx + 1);
      setState(s => ({ phase: "loading", version: s.version }));
      return;
    }
    fetch(url, { credentials: "include", cache: "no-store" }).then(r => {
      // Iter 586 — 404 = file memang tidak ada → "Mockup tidak tersedia" (Offline & Shopee).
      //   429/5xx = gangguan sementara penyimpanan → coba ulang otomatis 1x, lalu tampilkan "Coba lagi".
      if (r.status === 404) { setState(s => ({ phase: "empty", version: s.version })); return; }
      if ((r.ok || r.status === 429 || r.status >= 500) && !autoRetried.current) {
        autoRetried.current = true;
        setTimeout(() => { setAltIdx(0); setState(s => ({ phase: "loading", version: s.version + 1 })); }, 2500);
        return;
      }
      setState(s => ({ phase: "error", version: s.version, busy: r.status === 503 || r.status === 429 }));
    }).catch(() => setState(s => ({ phase: "error", version: s.version })));
  };
  // Iter 519 — Timeout 15s: hanya aktif setelah elemen masuk viewport (fetch
  //   lazy baru benar-benar dimulai saat img terlihat). IntersectionObserver
  //   men-track visibility; timer di-set saat (visible && phase==="loading").
  useEffect(() => {
    const el = btnRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      // Fallback bila IO tidak tersedia (jsdom test): langsung start timer.
      if (state.phase === "loading") {
        clearTimer();
        timerRef.current = setTimeout(() => {
          setState(s => s.phase === "loading" ? { ...s, phase: "error" } : s);
        }, 15000);
      }
      return () => clearTimer();
    }
    const io = new IntersectionObserver((entries) => {
      const vis = entries.some(e => e.isIntersecting);
      visibleRef.current = vis;
      if (vis && state.phase === "loading" && !timerRef.current) {
        timerRef.current = setTimeout(() => {
          setState(s => s.phase === "loading" ? { ...s, phase: "error" } : s);
        }, 15000);
      }
      if (!vis) clearTimer();
    }, { rootMargin: "100px" });
    io.observe(el);
    return () => { try { io.disconnect(); } catch {} clearTimer(); };
  }, [state.phase, state.version]);
  return (
    <button type="button" ref={btnRef} className={`monitor-v3-thumb mv3-thumb-${state.phase}`} onClick={onClick}
      data-testid={`monitor-thumb-${orderNumber}`}
      aria-label={state.phase === "error" ? "Coba muat ulang mockup" : "Pratinjau mockup"}
      title={state.phase === "error" ? "Klik untuk coba lagi" : state.phase === "ok" ? "Klik untuk preview" : ""}>
      {state.phase === "empty" ? (
        <span className="monitor-v3-thumb-ph" data-testid={`monitor-thumb-empty-${orderNumber}`}>{shopeeEmpty ? (emptyReason || "Belum ada mockup — desain belum FIX") : "Mockup tidak tersedia"}</span>
      ) : state.phase === "error" ? (
        <span className="monitor-v3-thumb-ph monitor-v3-thumb-err" data-testid={`monitor-thumb-error-${orderNumber}`}>{state.busy ? "Penyimpanan sedang sibuk" : "Gagal memuat"}<br/><b>Coba lagi</b></span>
      ) : (
        <>
          <img key={state.version} src={url} alt="" draggable="false" loading="lazy" decoding="async"
            onLoad={() => { clearTimer(); setState(s => ({ phase: "ok", version: s.version })); }}
            onError={onError}/>
          {state.phase === "loading" && <span className="monitor-v3-thumb-spin"/>}
        </>
      )}
    </button>
  );
}
